import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { orders as ordersApi, services } from '../api/endpoints';
import { useOrderEvents } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  Loading, Empty, StatusBadge, Pagination, Modal, Can, money, dateTime,
} from '../components/Common';

const STATUSES = [
  'placed', 'confirmed', 'pickup-assigned', 'out-for-pickup', 'picked-up',
  'at-store', 'in-process', 'ready', 'out-for-delivery', 'delivered', 'cancelled',
];

export default function Orders() {
  const { can, storeId } = useAuth();
  const toast = useToast();

  const [data, setData] = useState({ orders: [], meta: null });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '', status: '', type: '', page: 1 });
  const [walkInOpen, setWalkInOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = { page: filters.page, limit: 20 };
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.type) params.type = filters.type;

      const res = await ordersApi.list(params);
      setData(res.data);
    } catch (err) {
      toast.error('Could not load orders', err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  useEffect(() => { load(); }, [load]);

  // Keep the list live - a new booking appears at the top on its own.
  useOrderEvents(() => load());

  useEffect(() => { document.title = 'Orders · UrbanClean'; }, []);

  const setStatus = async (id, status) => {
    try {
      await ordersApi.setStatus(id, { status });
      toast.success('Status updated');
      load();
    } catch (err) {
      toast.error('Could not update status', err.message);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Orders</h1>
          <div className="sub">Live order queue — updates arrive automatically</div>
        </div>
        <Can permission="order.create">
          <button className="btn btn-blue" onClick={() => setWalkInOpen(true)}>+ Walk-in order</button>
        </Can>
      </div>

      <div className="toolbar">
        <input
          className="input"
          placeholder="Search order no, name or mobile"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value, page: 1 }))}
          style={{ minWidth: 250 }}
        />
        <select
          className="select"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{s.replace(/-/g, ' ')}</option>
          ))}
        </select>
        <select
          className="select"
          value={filters.type}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value, page: 1 }))}
        >
          <option value="">App &amp; walk-in</option>
          <option value="app">App orders</option>
          <option value="walk-in">Walk-in orders</option>
        </select>
        <div className="spacer" />
        <button className="btn btn-ghost btn-sm" onClick={load}>Refresh</button>
      </div>

      <div className="card">
        {loading ? <Loading /> : data.orders.length === 0 ? (
          <Empty title="No orders match this filter" body="Try clearing the search or status filter." />
        ) : (
          <>
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Order</th><th>Customer</th><th>Items</th><th>Status</th>
                    <th>Payment</th><th className="right">Total</th><th>Placed</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o._id}>
                      <td>
                        <Link to={`/orders/${o._id}`} className="mono strong">{o.orderNumber}</Link>
                        <div style={{ marginTop: 3 }}>
                          <span className="badge badge-gray">{o.type === 'walk-in' ? 'Walk-in' : 'App'}</span>
                          {o.isExpress ? <span className="badge badge-amber" style={{ marginLeft: 4 }}>Express</span> : null}
                        </div>
                      </td>
                      <td>
                        {o.customerName || '—'}
                        <div className="muted" style={{ fontSize: 12 }}>{o.customerMobile}</div>
                      </td>
                      <td className="muted">
                        {o.items?.length
                          ? `${o.items.length} item(s)`
                          : o.serviceName || o.packages?.map((p) => p.name).join(', ') || '—'}
                      </td>
                      <td><StatusBadge status={o.status} /></td>
                      <td>
                        <span className={`badge badge-${o.paymentStatus === 'paid' ? 'green' : 'amber'}`}>
                          {o.paymentStatus}
                        </span>
                        <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>
                          {o.paymentMode?.toUpperCase()}
                        </div>
                      </td>
                      <td className="right strong">
                        {money(o.total)}
                        {/* Provisional until the store counts the items - the
                            customer sees no price at all while it says this. */}
                        {o.billStatus === 'confirmed' ? null : (
                          <div className="muted" style={{ fontSize: 11, fontWeight: 500, marginTop: 2 }}>
                            bill pending
                          </div>
                        )}
                      </td>
                      <td className="muted nowrap">{dateTime(o.createdAt)}</td>
                      <td>
                        {can('order.update') && !['delivered', 'cancelled'].includes(o.status) ? (
                          <select
                            className="select"
                            style={{ minWidth: 148, fontSize: 12.5, padding: '6px 8px' }}
                            value={o.status}
                            onChange={(e) => setStatus(o._id, e.target.value)}
                          >
                            {STATUSES.map((s) => (
                              <option key={s} value={s}>{s.replace(/-/g, ' ')}</option>
                            ))}
                          </select>
                        ) : (
                          <Link className="btn btn-sm btn-ghost" to={`/orders/${o._id}`}>Open</Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination meta={data.meta} onPage={(page) => setFilters((f) => ({ ...f, page }))} />
          </>
        )}
      </div>

      <WalkInModal
        open={walkInOpen}
        storeId={storeId}
        onClose={() => setWalkInOpen(false)}
        onCreated={() => { setWalkInOpen(false); load(); }}
      />
    </>
  );
}

/** Counter order: find-or-create the customer, then build the basket. */
function WalkInModal({ open, onClose, onCreated, storeId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [basket, setBasket] = useState({});
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [isExpress, setIsExpress] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cod');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    services.items().then((res) => setItems(res.data.items)).catch(() => {});
    setBasket({}); setMobile(''); setName(''); setIsExpress(false);
  }, [open]);

  const setQty = (id, qty) => {
    setBasket((b) => {
      const next = { ...b };
      if (qty <= 0) delete next[id];
      else next[id] = qty;
      return next;
    });
  };

  const lines = Object.entries(basket).map(([id, qty]) => {
    const item = items.find((i) => i._id === id);
    return { id, qty, item };
  }).filter((l) => l.item);

  const subtotal = lines.reduce((sum, l) => sum + l.item.price * l.qty, 0);

  const submit = async () => {
    if (!mobile) return toast.error('Enter the customer mobile number');
    if (!lines.length) return toast.error('Add at least one item');

    setBusy(true);
    try {
      await ordersApi.walkIn({
        customerMobile: mobile,
        customerName: name,
        items: lines.map((l) => ({ item: l.id, quantity: l.qty })),
        isExpress,
        paymentMode,
        store: storeId || undefined,
      });
      toast.success('Walk-in order created');
      onCreated();
    } catch (err) {
      toast.error('Could not create the order', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="New walk-in order"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : `Create order · ${money(subtotal)}`}
          </button>
        </>
      }
    >
      <div className="row">
        <div className="field">
          <label>Customer mobile *</label>
          <input className="input" value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" />
          <div className="hint">An existing customer with this number is reused automatically.</div>
        </div>
        <div className="field">
          <label>Customer name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
        </div>
      </div>

      <div className="row">
        <div className="field">
          <label>Payment mode</label>
          <select className="select" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
            <option value="cod">Cash</option>
            <option value="online">Online</option>
          </select>
        </div>
        <div className="field">
          <label>Turnaround</label>
          <label className="switch" style={{ marginTop: 8 }}>
            <input type="checkbox" checked={isExpress} onChange={(e) => setIsExpress(e.target.checked)} />
            <span className="track" />
            <span>Express service</span>
          </label>
        </div>
      </div>

      <div className="field">
        <label>Items</label>
        <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
          <table className="data">
            <thead>
              <tr><th>Service</th><th className="right">Rate</th><th className="center" style={{ width: 130 }}>Qty</th></tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item._id}>
                  <td>
                    {item.name}
                    <div className="muted" style={{ fontSize: 11.5 }}>{item.category?.name} · per {item.unit}</div>
                  </td>
                  <td className="right">{money(item.price)}</td>
                  <td className="center">
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                      <button className="btn btn-sm btn-ghost" onClick={() => setQty(item._id, (basket[item._id] || 0) - 1)}>−</button>
                      <span style={{ minWidth: 20 }}>{basket[item._id] || 0}</span>
                      <button className="btn btn-sm btn-ghost" onClick={() => setQty(item._id, (basket[item._id] || 0) + 1)}>+</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {lines.length > 0 ? (
        <div className="card" style={{ background: 'var(--blue-50)' }}>
          <div className="card-body" style={{ padding: 14 }}>
            {lines.map((l) => (
              <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                <span>{l.item.name} × {l.qty}</span>
                <span className="strong">{money(l.item.price * l.qty)}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--line)', paddingTop: 8, marginTop: 8 }}>
              <span className="strong">Subtotal (GST added on save)</span>
              <span className="strong">{money(subtotal)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
